// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Turning a page of positioned glyph runs back into a document.
 *
 * Everything here is a pure function over a list of items with coordinates, so
 * the whole layout engine is unit-testable without loading pdf.js at all —
 * which matters, because the failure modes (a running header glued onto every
 * paragraph, a hyphen left inside a word, four paragraphs merged into one
 * block) are all layout bugs rather than PDF bugs, and they are the ones that
 * quietly poison a retrieval score.
 *
 * The output is deliberately more than a string:
 *   text        one character stream, the coordinate system for everything else
 *   pages       char span per page, so a chunk can say which page it came from
 *   paragraphs  the STRATEGY-INDEPENDENT partition — the natural boundaries the
 *               recursive splitter prefers, and the units the interface reads
 *   headings    char spans with a level, so heading-aware chunking has
 *               something real to attach to rather than a regex over prose
 */

export interface RawItem {
  str: string;
  /** transform[4] */
  x: number;
  /** transform[5]; PDF space, so larger is further UP the page */
  y: number;
  width: number;
  /** Rendered type size — `height`, or the transform's scale for rotated text. */
  size: number;
  /** 1-based. */
  page: number;
  /** Position in the content stream, which is the producer's own reading order. */
  order: number;
}

export interface Span {
  start: number;
  end: number;
}

export interface Heading extends Span {
  level: number;
  text: string;
}

/**
 * How the lines were put in order.
 *
 * `document` means the producer's own content-stream order was used, which is
 * correct for the overwhelming majority of PDFs INCLUDING real two-column ones:
 * measured on a genuine two-column paper, the typesetter emits the left column
 * contiguously and then the right, so document order is already reading order
 * and sorting by y is what would interleave them. `geometric` means that order
 * was visibly shuffled and the page was sorted top-to-bottom instead.
 */
export type LineOrder = 'document' | 'geometric';

export interface ExtractedDoc {
  name: string;
  text: string;
  pages: Span[];
  paragraphs: Span[];
  headings: Heading[];
  /** How many lines were dropped as running headers or footers. */
  runningLinesDropped: number;
  lineOrder: LineOrder;
  /** False when the file carried no extractable text layer at all. */
  hasTextLayer: boolean;
}

interface Line {
  text: string;
  x: number;
  right: number;
  y: number;
  size: number;
  page: number;
  order: number;
}

const SPACE = String.fromCharCode(32);
const BAR = String.fromCharCode(124);
/* Format characters and the byte-order mark. pdf.js does not remove these: it
 * drops a glyph whose ToUnicode string ENDS in one — losing visible text with
 * it — and passes a non-terminal one through verbatim. U+FEFF is separately
 * nasty because JavaScript's \s matches it while Unicode White_Space does not,
 * so it arrives as a literal word-splitting space. Built from a source string so
 * no literal control character appears in this file. */
const FORMAT_CHARS = new RegExp('[\\p{Cf}]', 'gu');

/** Median of a numeric list. Returns 0 for an empty list rather than NaN. */
export function median(xs: number[]): number {
  if (xs.length === 0) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const mid = s.length >> 1;
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/**
 * Group a page's items into lines by their baseline.
 *
 * The tolerance is relative to the type size rather than absolute: 1 pt of drift
 * is nothing in a 20 pt heading and is a separate line in 6 pt small print.
 *
 * Each line remembers the lowest content-stream index it contains, which is what
 * lets the caller keep the producer's reading order instead of imposing one.
 */
export function groupLines(items: RawItem[]): Line[] {
  const real = items.filter((it) => it.str.length > 0);
  if (real.length === 0) return [];
  const sizes = real.map((it) => it.size).filter((s) => s > 0);
  const tol = Math.max(1, median(sizes) * 0.4);

  const sorted = [...real].sort((a, b) => b.y - a.y || a.x - b.x);
  const rows: RawItem[][] = [];
  let cur: RawItem[] = [sorted[0]];
  let anchor = sorted[0].y;
  for (let i = 1; i < sorted.length; i++) {
    const it = sorted[i];
    if (Math.abs(it.y - anchor) <= tol) {
      cur.push(it);
    } else {
      rows.push(cur);
      cur = [it];
      anchor = it.y;
    }
  }
  rows.push(cur);

  return rows
    .map((row) => {
      row.sort((a, b) => a.x - b.x);
      let text = '';
      let prevRight = Number.NaN;
      let maxSize = 0;
      let order = Number.MAX_SAFE_INTEGER;
      for (const it of row) {
        if (it.size > maxSize) maxSize = it.size;
        if (it.order < order) order = it.order;
        if (!Number.isNaN(prevRight)) {
          const gap = it.x - prevRight;
          // A run break with no gap is a font change mid-word, not a space. The
          // threshold is a fraction of the type size for the same reason the
          // line tolerance is.
          if (gap > it.size * 0.18 && !/\s$/.test(text)) text += SPACE;
        }
        text += it.str;
        prevRight = it.x + it.width;
      }
      const first = row[0];
      return {
        text: text.replace(/\s+/g, SPACE).trim(),
        x: first.x,
        right: prevRight,
        y: first.y,
        size: maxSize,
        page: first.page,
        order,
      };
    })
    .filter((l) => l.text.length > 0);
}

/**
 * Put a page's lines in reading order.
 *
 * The default is the producer's own content-stream order, and that is a
 * correction rather than a shortcut. Sorting by y is the obvious thing to do and
 * it is what BREAKS a two-column paper: real two-column producers emit the left
 * column contiguously and then the right, so a y-sort interleaves two unrelated
 * columns line by line and every chunk comes out half nonsense. Column detectors
 * built to repair that damage were measured finding zero columns on a genuine
 * two-column paper and ten to sixteen spurious ones on pages containing tables,
 * where the "gutter" was a cell boundary — so no column detector ships here.
 *
 * Content-stream order is wrong only for producers that emit text out of order,
 * which is why it is checked rather than assumed: if the y values of consecutive
 * lines are not mostly descending, the page is visibly shuffled and a geometric
 * sort is the better guess.
 */
export function orderLines(lines: Line[]): { lines: Line[]; order: LineOrder } {
  if (lines.length < 3) return { lines, order: 'document' };
  const inDocOrder = [...lines].sort((a, b) => a.order - b.order);
  let descending = 0;
  let comparable = 0;
  for (let i = 1; i < inDocOrder.length; i++) {
    const prev = inDocOrder[i - 1];
    const cur = inDocOrder[i];
    if (prev.page !== cur.page) continue;
    comparable += 1;
    if (cur.y <= prev.y) descending += 1;
  }
  if (comparable === 0) return { lines: inDocOrder, order: 'document' };
  const violations = comparable - descending;
  // An ALLOWANCE, not a proportion. Every ordinary page contains a small fixed
  // number of upward jumps — the footer drawn before the body, a column break —
  // and a proportion punishes short pages for them: the sample's five-line title
  // page has exactly one such jump and scored 0.75, below any sensible ratio,
  // while a forty-line page with the same single jump scored 0.98. A scrambled
  // stream produces violations by the dozen, so a small absolute allowance
  // separates the two cleanly and a ratio does not.
  if (violations <= Math.max(3, comparable * 0.15)) return { lines: inDocOrder, order: 'document' };
  return { lines: [...lines].sort((a, b) => b.y - a.y || a.x - b.x), order: 'geometric' };
}

/** Digits are the only thing that varies in "Page 7 of 42", so mask them. */
function runningKey(line: Line): string {
  // The TYPE SIZE is part of the identity. A section title and the running
  // header that repeats it are frequently the same words — 20 pt on the opening
  // page, 8 pt at the top of every page after it — and a key made of text alone
  // strips the title along with the header, losing the document's top-level
  // structure. Rounding to the nearest half point tolerates sub-pixel drift.
  const size = Math.round(line.size * 2) / 2;
  return size + BAR + line.text.replace(/\d+/g, '#').trim().toLowerCase();
}

/**
 * Drop lines that repeat at the same place across pages.
 *
 * A running header is not a nuisance to be tidied. It is text that would
 * otherwise be glued to the top of a chunk on every single page, adding the same
 * words to every vector and flattening exactly the differences retrieval depends
 * on.
 *
 * Two rules, either of which is enough. The document-wide count catches the
 * header that is identical on every page. The CONSECUTIVE-RUN rule catches the
 * one that changes per section — "Part A - Working here" for seven pages, then
 * "Part B - Time off" for six — which is how long documents are actually laid
 * out and which never reaches a document-wide proportion. Measured on the
 * shipped sample, the count rule alone left the section title on seven pages.
 */
export function stripRunning(pages: Line[][]): { pages: Line[][]; dropped: number } {
  if (pages.length < 4) return { pages, dropped: 0 };

  /*
   * Which band a line sits in, measured GEOMETRICALLY rather than by position in
   * the list.
   *
   * An index-based band ("the first two and last two lines") assumes the list is
   * in visual order, and it is not: lines are kept in the producer's
   * content-stream order, and a generator that draws its header, then its footer,
   * then the body puts the page number at index 1 on one page and somewhere else
   * on the next. Measured on the shipped sample, the index rule leaked exactly
   * one "Page 1" through — one page whose stream order happened to differ.
   * Top and bottom of the page are what "running header" actually means.
   */
  const bandOf = (lines: Line[], i: number): string | null => {
    if (lines.length < 4) return null;
    const ys = lines.map((l) => l.y);
    const top = Math.max(...ys);
    const bottom = Math.min(...ys);
    const span = top - bottom;
    if (span <= 0) return null;
    const y = lines[i].y;
    if (y >= top - span * 0.1) return 'head';
    if (y <= bottom + span * 0.1) return 'foot';
    return null;
  };

  const counts = new Map<string, number>();
  for (const lines of pages) {
    lines.forEach((l, i) => {
      const band = bandOf(lines, i);
      if (band) {
        const k = band + BAR + runningKey(l);
        counts.set(k, (counts.get(k) ?? 0) + 1);
      }
    });
  }

  const runKeys = new Set<string>();
  for (const band of ['head', 'foot']) {
    const seq = pages.map((lines) => {
      // The topmost (or bottommost) line on the page, again by geometry.
      let pick: Line | null = null;
      for (let i = 0; i < lines.length; i++) {
        if (bandOf(lines, i) !== band) continue;
        if (!pick) pick = lines[i];
        else if (band === 'head' ? lines[i].y > pick.y : lines[i].y < pick.y) pick = lines[i];
      }
      return pick ? runningKey(pick) : null;
    });
    let start = 0;
    for (let i = 1; i <= seq.length; i++) {
      if (i === seq.length || seq[i] !== seq[start]) {
        if (seq[start] !== null && i - start >= 3) runKeys.add(band + BAR + seq[start]);
        start = i;
      }
    }
  }

  const threshold = Math.max(3, Math.ceil(pages.length * 0.6));
  let dropped = 0;
  const out = pages.map((lines) =>
    lines.filter((l, i) => {
      const band = bandOf(lines, i);
      if (!band) return true;
      const key = band + BAR + runningKey(l);
      if ((counts.get(key) ?? 0) >= threshold || runKeys.has(key)) {
        dropped += 1;
        return false;
      }
      return true;
    }),
  );
  return { pages: out, dropped };
}

const TERMINAL = /[.!?:;'")\]]$/;

/**
 * Classify each line as a heading and give it a level.
 *
 * Font size is the signal, measured against the body size — which is the size
 * that carries the most CHARACTERS, not the most lines, because a document with
 * many short headings and few long paragraphs would otherwise elect a heading
 * size as its body.
 *
 * On a densely typeset academic paper this separates poorly: real documents have
 * been measured with twenty-two distinct type sizes on a page. Heading-aware
 * chunking then degrades toward the recursive splitter, which is the right
 * failure — it does not invent structure that is not there.
 */
export function detectHeadings(lines: Line[]): Map<number, number> {
  const charsBySize = new Map<number, number>();
  for (const l of lines) {
    const k = Math.round(l.size * 2) / 2;
    charsBySize.set(k, (charsBySize.get(k) ?? 0) + l.text.length);
  }
  let bodySize = 0;
  let bodyChars = -1;
  charsBySize.forEach((chars, size) => {
    if (chars > bodyChars) {
      bodyChars = chars;
      bodySize = size;
    }
  });

  const bigger = [...new Set(lines.map((l) => Math.round(l.size * 2) / 2))]
    .filter((s) => s > bodySize * 1.12)
    .sort((a, b) => b - a);

  const levels = new Map<number, number>();
  lines.forEach((l, i) => {
    const size = Math.round(l.size * 2) / 2;
    const rank = bigger.indexOf(size);
    if (rank >= 0) {
      levels.set(i, Math.min(6, rank + 2));
      return;
    }
    // A same-size heading: short, unpunctuated, and not the tail of a paragraph.
    if (
      size >= bodySize &&
      l.text.length <= 72 &&
      !TERMINAL.test(l.text) &&
      /[A-Za-z]/.test(l.text) &&
      l.text.split(SPACE).length <= 10
    ) {
      const prev = lines[i - 1];
      if (!prev || TERMINAL.test(prev.text) || prev.page !== l.page) {
        levels.set(i, bigger.length > 0 ? Math.min(6, bigger.length + 2) : 3);
      }
    }
  });
  return levels;
}

/**
 * Assemble ordered lines into a character stream with paragraph, page and
 * heading spans.
 */
export function assemble(name: string, pageLines: Line[][], lineOrder: LineOrder, dropped: number): ExtractedDoc {
  const flat: Line[] = [];
  for (const lines of pageLines) for (const l of lines) flat.push(l);
  const headingLevels = detectHeadings(flat);

  // Typical line pitch, used to tell a paragraph break from ordinary leading.
  const gaps: number[] = [];
  for (let i = 1; i < flat.length; i++) {
    if (flat[i].page === flat[i - 1].page) {
      const d = flat[i - 1].y - flat[i].y;
      if (d > 0) gaps.push(d);
    }
  }
  const pitch = median(gaps) || 12;
  const bodyRight = median(flat.map((l) => l.right));

  let text = '';
  const pages: Span[] = [];
  const paragraphs: Span[] = [];
  const headings: Heading[] = [];

  let pageStart = 0;
  let paraStart = 0;
  let paraOpen = false;
  let paraIsHeading = false;

  const closeParagraph = () => {
    // A heading is a block of text but it is not a paragraph. Letting it into
    // the partition would make every heading a unit of its own in anything
    // fitted over paragraphs, and it is not prose.
    if (paraOpen && !paraIsHeading && text.length > paraStart) {
      paragraphs.push({ start: paraStart, end: text.length });
    }
    paraOpen = false;
  };

  for (let i = 0; i < flat.length; i++) {
    const l = flat[i];
    const prev = flat[i - 1];

    if (prev && l.page !== prev.page) {
      pages.push({ start: pageStart, end: text.length });
      pageStart = text.length;
    }

    const level = headingLevels.get(i);
    let breakParagraph = !prev || level !== undefined || headingLevels.has(i - 1);
    if (prev && !breakParagraph) {
      if (l.page === prev.page) {
        const gap = prev.y - l.y;
        // 1.3, not 1.55. Ordinary body leading with a paragraph space after it
        // is commonly only about 1.4x the line pitch, and at 1.55 the shipped
        // sample merged four consecutive paragraphs into one 1,537-character
        // block — which silently moves every chunk boundary in the document.
        //
        // A NEGATIVE gap means y jumped back up: a column break, not a paragraph
        // break, and treating it as one would split mid-sentence.
        if (gap > pitch * 1.3) breakParagraph = true;
        // An indent after a line that ended a sentence is the other classic
        // signal, and the only one available in tightly-leaded typesetting.
        if (!breakParagraph && l.x > prev.x + l.size * 0.8 && TERMINAL.test(prev.text)) breakParagraph = true;
      }
      // A SHORT previous line that ended a sentence is the last line of a
      // paragraph — the one rule that needs no leading measurement at all, and
      // the one that survives a page break.
      if (!breakParagraph && TERMINAL.test(prev.text) && prev.right < bodyRight * 0.9) {
        breakParagraph = true;
      }
    }

    if (breakParagraph) {
      closeParagraph();
      if (text.length > 0) text += '\n\n';
      paraStart = text.length;
      paraOpen = true;
      paraIsHeading = level !== undefined;
    } else if (prev) {
      // Hyphenation repair: a trailing hyphen before a lowercase continuation is
      // a word broken by the typesetter, not a compound.
      if (/[A-Za-z]-$/.test(text) && /^[a-z]/.test(l.text)) {
        text = text.slice(0, -1);
      } else {
        text += SPACE;
      }
    }

    const start = text.length;
    text += l.text;
    if (level !== undefined) {
      headings.push({ start, end: text.length, level, text: l.text });
    }
  }

  closeParagraph();
  pages.push({ start: pageStart, end: text.length });

  return {
    name,
    text,
    pages,
    paragraphs,
    headings,
    runningLinesDropped: dropped,
    lineOrder,
    hasTextLayer: text.trim().length > 0,
  };
}

/** The whole pipeline, from positioned items to a document. */
export function layout(name: string, items: RawItem[], pageCount: number): ExtractedDoc {
  const byPage: RawItem[][] = Array.from({ length: Math.max(1, pageCount) }, () => []);
  for (const it of items) {
    const idx = Math.min(byPage.length - 1, Math.max(0, it.page - 1));
    byPage[idx].push(it);
  }
  let lineOrder: LineOrder = 'document';
  const laid = byPage.map((pageItems) => {
    const ordered = orderLines(groupLines(pageItems));
    if (ordered.order === 'geometric') lineOrder = 'geometric';
    return ordered.lines;
  });
  const stripped = stripRunning(laid);
  return assemble(name, stripped.pages, lineOrder, stripped.dropped);
}

/** Plain text and Markdown need no layout — but they still need the same spans. */
export function fromPlainText(name: string, raw: string, markdown: boolean): ExtractedDoc {
  const text = raw.replace(/\r\n?/g, '\n');
  const paragraphs: Span[] = [];
  const headings: Heading[] = [];

  const re = /[^\n]+(?:\n(?!\s*\n)[^\n]+)*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const block = m[0];
    const firstLine = block.split('\n')[0];
    const hash = markdown ? /^(#{1,6})\s+(.*)$/.exec(firstLine) : null;
    if (hash) {
      const firstLineEnd = start + firstLine.length;
      headings.push({ start, end: firstLineEnd, level: hash[1].length, text: hash[2].trim() });
      if (firstLineEnd < end) paragraphs.push({ start: firstLineEnd + 1, end });
    } else {
      paragraphs.push({ start, end });
    }
  }

  return {
    name,
    text,
    pages: [{ start: 0, end: text.length }],
    paragraphs,
    headings,
    runningLinesDropped: 0,
    lineOrder: 'document',
    hasTextLayer: text.trim().length > 0,
  };
}

interface TextItemLike {
  str?: string;
  width?: number;
  height?: number;
  transform?: number[];
}

/**
 * Map one page's `getTextContent().items` onto positioned runs.
 *
 * It lives here rather than in the pdf.js adapter so the test suite can feed real
 * pages through the real mapping without importing the browser build of pdf.js,
 * which needs `DOMMatrix` and most of a canvas implementation before it will even
 * load.
 *
 * Two details that a plainer version gets wrong. The type size is
 * `hypot(transform[2], transform[3])`, never `transform[3]` on its own: for text
 * rotated 90 degrees `transform[3]` is exactly 0, and every geometric threshold
 * downstream then divides by nothing. And format characters are stripped HERE,
 * because pdf.js leaves a non-terminal one in the string verbatim — a zero-width
 * space inside a word makes it a different word to every tokenizer.
 */
export function itemsFromTextContent(items: readonly unknown[], page: number): RawItem[] {
  const out: RawItem[] = [];
  // The reading order is (page, position on page), encoded in one number so a
  // caller cannot get it wrong by forgetting to carry a running counter across
  // pages — which silently restarts the order on every page and interleaves the
  // whole document. A page with a million text runs does not exist.
  let order = page * 1_000_000;
  for (const raw of items as TextItemLike[]) {
    const original = raw?.str ?? '';
    order += 1;
    if (!original) continue;
    const str = original.replace(FORMAT_CHARS, '');
    if (!str || !str.trim()) continue;
    const t = raw.transform;
    if (!t || t.length < 6) continue;
    const scaled = Math.hypot(t[2] ?? 0, t[3] ?? 0);
    out.push({
      str,
      x: t[4],
      y: t[5],
      width: raw.width ?? 0,
      size: raw.height && raw.height > 0 ? raw.height : scaled || 10,
      page,
      order,
    });
  }
  return out;
}

/** Which 1-based page a character offset falls on. */
export function pageOf(doc: ExtractedDoc, offset: number): number {
  for (let i = 0; i < doc.pages.length; i++) {
    if (offset < doc.pages[i].end) return i + 1;
  }
  return doc.pages.length;
}
